import React, { useState } from "react";
import { HStack, Badge, Button, useToast } from "@chakra-ui/react";
import { STATUS_META, ACTION_LABELS, DECISION_ACTIONS, availableActions, canEditTask } from "../../utils/workItemStatus";
import { transitionWorkItem, reassignWorkItem, transitionErrorMessage } from "../../services/workItemsService";
import WorkItemDecisionModal from "./WorkItemDecisionModal";

const ACTION_STYLE = {
  complete: { colorScheme: "green", variant: "outline" },
  confirm: { colorScheme: "green", variant: "solid" },
  send_back: { colorScheme: "orange", variant: "outline" },
  reopen: { colorScheme: "gray", variant: "ghost" },
  start: { colorScheme: "blue", variant: "outline" },
  stop: { colorScheme: "gray", variant: "ghost" },
  reject: { colorScheme: "red", variant: "ghost" },
  reassign: { colorScheme: "blue", variant: "ghost" },
  cancel: { colorScheme: "gray", variant: "ghost" },
  unassign: { colorScheme: "gray", variant: "ghost" },
  comment: { colorScheme: "blue", variant: "ghost" },
  edit: { colorScheme: "blue", variant: "ghost" },
};

// item: { kind, id, schema?, status (normalized), assignedTo, assignedBy, title? }
// currentUserSchema scopes Reassign's people search to the user's clinic group.
// onComment: when given, the person it's assigned to and the person who
// assigned it get a "Comment" button; the host opens its comment box.
// onEdit: when given, the person who assigned a task or order gets an "Edit"
// button (a task's title, description, due date, priority; an order's text
// and priority); the host opens its editor.
export default function WorkItemActions({
  item, currentUserId, currentUserSchema, onChanged, onComment, onEdit, size = "xs", showStatus = true,
}) {
  const [busy, setBusy] = useState(null);
  const [deciding, setDeciding] = useState(null); // the action whose window is open
  const toast = useToast();
  const meta = STATUS_META[item.status] || STATUS_META.unassigned;
  const canComment = !!onComment && !!currentUserId
    && (currentUserId === item.assignedTo || currentUserId === item.assignedBy);
  const canEdit = !!onEdit && canEditTask(item, currentUserId);

  const fail = (err) =>
    toast({ title: "Couldn't update", description: transitionErrorMessage(err), status: "error", duration: 5000 });

  const run = async (action) => {
    setBusy(action);
    try {
      const { newStatus } = await transitionWorkItem(item, action, currentUserId);
      onChanged?.(newStatus);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  // Resolves false so the window stays open after a failure.
  const decide = async ({ message, toUser }) => {
    try {
      if (deciding === "reassign") {
        const res = await reassignWorkItem(item, currentUserId, toUser.id, message);
        onChanged?.(res?.item?.status, res?.item);
      } else {
        const { newStatus } = await transitionWorkItem(item, deciding, currentUserId, { message });
        onChanged?.(newStatus);
      }
      return true;
    } catch (err) {
      fail(err);
      return false;
    }
  };

  return (
    <HStack spacing={1} flexWrap="wrap">
      {showStatus && (
        <Badge colorScheme={meta.colorScheme} borderRadius="full" px={2} data-testid="work-item-status">
          {meta.label}
        </Badge>
      )}
      {availableActions(item, currentUserId).map((action) => (
        <Button key={action} size={size} {...ACTION_STYLE[action]} isLoading={busy === action}
          isDisabled={!!busy && busy !== action}
          onClick={() => (DECISION_ACTIONS.includes(action) ? setDeciding(action) : run(action))}>
          {ACTION_LABELS[action]}
        </Button>
      ))}
      {canEdit && (
        <Button size={size} {...ACTION_STYLE.edit} onClick={onEdit}
                leftIcon={<span aria-hidden="true">✏️</span>} iconSpacing={1}>
          Edit
        </Button>
      )}
      {canComment && (
        <Button size={size} {...ACTION_STYLE.comment} onClick={onComment}
                leftIcon={<span aria-hidden="true">💬</span>} iconSpacing={1}>
          Comment
        </Button>
      )}
      {deciding && (
        <WorkItemDecisionModal
          action={deciding}
          item={item}
          schema={currentUserSchema || item.schema || "public"}
          onClose={() => setDeciding(null)}
          onSubmit={decide}
        />
      )}
    </HStack>
  );
}
